import { useScrollReveal } from "../../../hooks/useScrollReveal";
import { Product } from "../../../mocks/products";
import DOMPurify from "dompurify";

interface Props {
  product: Product;
}

const SectionBlock = ({
  section,
  index,
}: {
  section: NonNullable<Product["sections"]>[number];
  index: number;
}) => {
  const { ref: imgRef, isVisible: imgVisible } = useScrollReveal(0.15);
  const { ref: txtRef, isVisible: txtVisible } = useScrollReveal(0.15);

  const isImageLeft = index % 2 === 0;

  const cleanHtml = section.content
    ? DOMPurify.sanitize(section.content, {
        ADD_TAGS: ["iframe"],
        ALLOWED_ATTR: [
          "href", "src", "alt", "title", "target", "rel",
          "style", "class", "width", "height", "data-size",
          "colspan", "rowspan", "allowfullscreen", "frameborder",
        ],
        FORCE_BODY: true,
      })
    : "";

  const isHtml = cleanHtml.includes("<");
  // Check if rich content has images - if so, give text side more space
  const contentHasImages = isHtml && cleanHtml.includes("<img");

  return (
    <div className={`py-16 ${index % 2 === 0 ? "bg-white" : "bg-stone-50"}`}>
      <div className="max-w-[1400px] mx-auto px-8">
        {/* 70:30 flex layout */}
        <div className="flex flex-col lg:flex-row gap-12 items-center">
          {/* Image side — flex: 7 (70%) or 5 (50%) if content has images */}
          <div
            ref={imgRef}
            className={`w-full ${isImageLeft ? "lg:order-1" : "lg:order-2"} transition-all duration-1000`}
            style={{
              flex: contentHasImages ? 5 : 7,
              minWidth: 0,
              opacity: imgVisible ? 1 : 0.95,
              transform: imgVisible
                ? "translateX(0) scale(1)"
                : isImageLeft
                ? "translateX(-20px) scale(0.99)"
                : "translateX(20px) scale(0.99)",
            }}
          >
            <div
              className="relative rounded-3xl overflow-hidden border-2 border-gray-300 shadow-md"
              style={{
                minHeight: 300,
                aspectRatio: "4 / 3",
                backgroundColor: "#E8ECF3",
                backgroundImage: "radial-gradient(circle, #D1D8E0 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            >
              {section.image ? (
                <img
                  src={section.image}
                  alt={section.title}
                  className="w-full h-full object-contain block p-3 md:p-5"
                  loading="eager"
                  decoding="sync"
                  style={{
                    display: "block",
                    imageRendering: "-webkit-optimize-contrast",
                    maxHeight: "100%",
                  }}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ minHeight: 300 }}>
                  <i className="ri-image-line text-5xl text-gray-400" />
                </div>
              )}
            </div>
          </div>

          {/* Text side — flex: 3 (30%) or 5 (50%) if content has images */}
          <div
            ref={txtRef}
            className={`w-full ${isImageLeft ? "lg:order-2" : "lg:order-1"} transition-all duration-1000 delay-200`}
            style={{
              flex: contentHasImages ? 5 : 3,
              minWidth: 0,
              opacity: txtVisible ? 1 : 0.95,
              transform: txtVisible
                ? "translateX(0)"
                : isImageLeft
                ? "translateX(20px)"
                : "translateX(-20px)",
            }}
          >
            {/* Number indicator */}
            <p className="text-[72px] font-bold text-stone-100 leading-none mb-2 select-none">
              {String(index + 1).padStart(2, "0")}
            </p>

            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-5 -mt-5">
              {section.title}
            </h3>

            <div className="w-10 h-1 bg-[#004A99] rounded-full mb-5" />

            {isHtml ? (
              <div
                className="text-gray-600 leading-relaxed w-full overflow-hidden
                  [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mb-3
                  [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mb-2
                  [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mb-2
                  [&_p]:mb-2.5 [&_p]:leading-relaxed [&_p]:text-sm
                  [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:mb-2.5 [&_ul>li]:mb-1 [&_ul>li]:text-sm
                  [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:mb-2.5 [&_ol>li]:mb-1 [&_ol>li]:text-sm
                  [&_strong]:font-bold [&_strong]:text-gray-900
                  [&_em]:italic
                  [&_a]:text-[#004A99] [&_a]:underline [&_a:hover]:text-[#003d7d]
                  [&_blockquote]:border-l-4 [&_blockquote]:border-[#004A99] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-500 [&_blockquote]:my-3
                  [&_img]:rounded-xl [&_img]:my-3 [&_img]:block [&_img]:max-w-full [&_img]:h-auto [&_img]:border [&_img]:border-gray-200 [&_img]:shadow-sm"
                style={{ maxWidth: '100%' }}
                dangerouslySetInnerHTML={{ __html: cleanHtml }}
              />
            ) : (
              <p className="text-gray-600 text-base leading-relaxed">{section.content}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductScrollStory = ({ product }: Props) => {
  if (!product.sections || product.sections.length === 0) return null;

  return (
    <div className="overflow-hidden">
      {/* Section header */}
      <div className="bg-gray-900 py-14 text-center">
        <p className="text-xs font-bold tracking-[0.3em] text-[#00AEEF] uppercase mb-3">
          Câu Chuyện Sản Phẩm
        </p>
        <h2 className="text-3xl font-bold text-white max-w-2xl mx-auto leading-tight">
          Khám phá {product.name}
        </h2>
      </div>

      {product.sections.map((section, i) => (
        <SectionBlock key={i} section={section} index={i} />
      ))}
    </div>
  );
};

export default ProductScrollStory;
